import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  useGetHomeQuery,
  useGetOnboardingStateQuery,
  useSubmitOnboardingMutation,
  useUpdateActiveOrganisationMutation,
} from "@/store/rest_api";
import type {
  BusinessBasics,
  OnboardingPath,
  OnboardingSubmission,
  PersonaAnswers,
} from "@/model/interface/Onboarding";
import BusinessBasicsStep from "./steps/BusinessBasicsStep";
import PersonaStep from "./steps/PersonaStep";
import IntegrationsStep from "./steps/IntegrationsStep";

const STEP_TITLES = ["Business basics", "About your work", "Integrations"];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { data: home } = useGetHomeQuery();
  const { data: onboardingState, isLoading: stateLoading } = useGetOnboardingStateQuery();
  const [updateOrg] = useUpdateActiveOrganisationMutation();
  const [submitOnboarding, { isLoading: isSubmitting }] = useSubmitOnboardingMutation();

  const orgName = home?.activeOrganisation?.name ?? "";

  const [stepIndex, setStepIndex] = useState(0);
  const path: OnboardingPath = "INVESTOR";
  const [basics, setBasics] = useState<BusinessBasics>({
    orgName: orgName,
    businessType: "",
    teamSize: "",
    country: "US",
    timezone: "America/New_York",
    marketFocus: "",
    useCase: "",
    phone: "",
  });
  const [persona, setPersona] = useState<PersonaAnswers>({
    leadSources: [],
    mlsMembership: false,
    licenseNumber: "",
  });
  const [integrations, setIntegrations] = useState<string[]>([]);

  const progress = ((stepIndex + 1) / STEP_TITLES.length) * 100;
  const isLastStep = stepIndex === STEP_TITLES.length - 1;
  const isSkippableStep = stepIndex === 2;

  const canAdvance = (): boolean => {
    if (stepIndex === 0) {
      return !!basics.orgName?.trim() && !!basics.teamSize;
    }
    return true;
  };

  const handleNext = async () => {
    if (stepIndex === 0) {
      try {
        await updateOrg({
          name: basics.orgName,
          phone: basics.phone || undefined,
          country: basics.country || "US",
          timezone: basics.timezone || "America/New_York",
          businessType: basics.businessType || undefined,
        }).unwrap();
      } catch {
        toast.error("Failed to save business info");
        return;
      }
    }
    setStepIndex((idx) => Math.min(idx + 1, STEP_TITLES.length - 1));
  };

  const handleBack = () => {
    setStepIndex((idx) => Math.max(idx - 1, 0));
  };

  const handleSkipStep = () => {
    setStepIndex((idx) => Math.min(idx + 1, STEP_TITLES.length - 1));
  };

  const handleFinish = async (skipIntegrations: boolean = false) => {
    const submission: OnboardingSubmission = {
      path,
      teamSize: basics.teamSize || null,
      marketFocus: basics.marketFocus || null,
      useCase: basics.useCase || null,
      leadSources: persona.leadSources.length > 0 ? persona.leadSources : null,
      mlsMembership: null,
      licenseNumber: persona.licenseNumber || null,
      integrationsUsed: !skipIntegrations && integrations.length > 0 ? integrations : null,
    };

    try {
      await submitOnboarding(submission).unwrap();
      toast.success("You're all set. Welcome!");
      navigate("/dashboard", { replace: true });
    } catch {
      toast.error("Failed to finish onboarding");
    }
  };

  useEffect(() => {
    if (orgName && !basics.orgName) {
      setBasics((prev: BusinessBasics) => ({ ...prev, orgName }));
    }
  }, [orgName, basics.orgName]);

  if (stateLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-lg">Loading…</div>
      </div>
    );
  }

  if (onboardingState?.completed) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-background rounded-lg shadow-lg p-8 space-y-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Step {stepIndex + 1} of {STEP_TITLES.length}
            </span>
            <span>{STEP_TITLES[stepIndex]}</span>
          </div>
          <Progress value={progress} />
        </div>

        <div className="min-h-[320px]">
          {stepIndex === 0 && <BusinessBasicsStep value={basics} onChange={setBasics} />}
          {stepIndex === 1 && (
            <PersonaStep
              path={path}
              value={persona}
              onChange={setPersona}
            />
          )}
          {stepIndex === 2 && (
            <IntegrationsStep value={integrations} onChange={setIntegrations} />
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="ghost" onClick={handleBack} disabled={stepIndex === 0}>
            Back
          </Button>
          <div className="flex items-center gap-2">
            {isSkippableStep && !isLastStep && (
              <Button variant="ghost" onClick={handleSkipStep}>
                Skip
              </Button>
            )}
            {isLastStep && (
              <Button variant="ghost" onClick={() => handleFinish(true)} disabled={isSubmitting}>
                Skip
              </Button>
            )}
            {!isLastStep && (
              <Button onClick={handleNext} disabled={!canAdvance()}>
                Next
              </Button>
            )}
            {isLastStep && (
              <Button onClick={() => handleFinish()} disabled={isSubmitting}>
                {isSubmitting ? "Finishing…" : "Finish"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
